import React, { useState, useEffect, useRef } from 'react';
import './VisualTutorial.css';
import { useProgress } from '../contexts/ProgressContext';

const VisualTutorial = ({ tutorial }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { updateTutorialProgress, getProgress } = useProgress();

  const totalSteps = tutorial.steps?.length || 0;
  const currentStepData = tutorial.steps?.[currentStep];

  // Load saved tutorial progress to resume current step 
  const hasLoadedInitialStep = useRef(false);
  useEffect(() => {
    if (hasLoadedInitialStep.current) return; // Only load once on mount
    
    try {
      const saved = getProgress('tutorials', tutorial.id);
      if (saved && typeof saved.currentStep === 'number') {
        const clamped = Math.min(Math.max(saved.currentStep, 0), totalSteps - 1);
        setCurrentStep(clamped);
      }
      hasLoadedInitialStep.current = true;
    } catch (_) {
      
      hasLoadedInitialStep.current = true;
    }
    
  }, [tutorial.id, totalSteps, getProgress]); 

  if (!currentStepData) return null;

  const isLastStep = currentStep === totalSteps - 1;

  const handleNext = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('handleNext called', { currentStep, totalSteps, tutorialId: tutorial.id });
    }
    
    // Mark current step as completed 
    try {
      if (updateTutorialProgress) {
        updateTutorialProgress(tutorial.id, currentStep, true);
      }
    } catch (error) {
      console.error('Error updating tutorial progress:', error);
    }
    
    // Always allow navigation 
    setCurrentStep(prevStep => {
      if (prevStep < totalSteps - 1) {
        const nextStep = prevStep + 1;
        if (process.env.NODE_ENV === 'development') {
          console.log('Moving to next step:', nextStep, 'from', prevStep);
        }
        return nextStep;
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('On last step, marking as complete');
        }
        return prevStep; // Stay on last step
      }
    });
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderContent = (content) => {
    // Simple markdown-like rendering
    const lines = content.split('\n');
    return lines.map((line, index) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        const text = line.slice(2, -2);
        return <strong key={index}>{text}</strong>;
      }
      if (line.startsWith('- ')) {
        return <li key={index}>{line.slice(2)}</li>;
      }
      if (line.trim() === '') {
        return <br key={index} />;
      }
      return <p key={index}>{line}</p>;
    });
  };

  return (
    <div className="visual-tutorial">
      <div className="visual-tutorial-header">
        <h1 className="visual-tutorial-title">{tutorial.title}</h1>
        <p className="visual-tutorial-description">{tutorial.description}</p>
        <div className="visual-tutorial-progress">
          <div className="progress-dots">
            {tutorial.steps.map((_, index) => (
              <div
                key={index}
                className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                onClick={() => setCurrentStep(index)}
              />
            ))}
          </div>
          <div className="progress-text">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
      </div>

      <div className="visual-tutorial-content">
        <div className="visual-step-content">
          <h2 className="visual-step-title">{currentStepData.title}</h2>
          <div className="visual-step-body">
            {renderContent(currentStepData.content)}
          </div>
        </div>

        {currentStepData.visual && (
          <div className="visual-step-visual">
            {currentStepData.visual}
          </div>
        )}
      </div>

      <div className="visual-tutorial-navigation">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 0}
          className="nav-button prev-button"
        >
          ← Previous
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="nav-button next-button"
        >
          {isLastStep ? 'Complete' : 'Next →'}
        </button>
      </div>
    </div>
  );
};

export default VisualTutorial;

